import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {Button, Card, Empty, Input, message, Popconfirm, Space, Tag, Tree, TreeSelect, Typography} from 'antd';
import {
  DeleteOutlined,
  FolderAddOutlined,
  FolderOpenOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  buildDirTree,
  composeDirectoryPath,
  containsEmoji,
  DirTreeNode,
  isSystemDirectory,
  mapDirTreeTitles,
  normalizeDirectoryName,
  normalizeShareDirectory,
  sortDirectories,
} from '../../utils/dirTree';
import {useTranslation} from '../../hooks/useTranslation';
import './index.css';

const {Text} = Typography;

export interface DirectoryManagerProps {
  directories: string[];
  loading?: boolean;
  onRefresh: () => Promise<void | string[]> | void;
  onAddDirectory: (dirName: string) => Promise<boolean> | boolean;
  onDeleteDirectory: (dirName: string) => Promise<boolean> | boolean;
  sharedDirectoryPaths?: Set<string>;
  renderSelectedActions?: (dirName: string) => React.ReactNode;
}

interface DirectoryTreeDataNode {
  key: string;
  title: React.ReactNode;
  children?: DirectoryTreeDataNode[];
}

const DirectoryManager: React.FC<DirectoryManagerProps> = ({
  directories,
  loading = false,
  onRefresh,
  onAddDirectory,
  onDeleteDirectory,
  sharedDirectoryPaths = new Set<string>(),
  renderSelectedActions,
}) => {
  const {t} = useTranslation();
  const [selectedDir, setSelectedDir] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [newDirParent, setNewDirParent] = useState('');
  const [newDirName, setNewDirName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const formatDirLabel = useCallback((value: string) => {
    if (value === '__all__' || value === '全部目录') return t('common.allDirectories');
    if (value === '__default__' || value === '默认目录') return t('common.defaultDirectory');
    return value;
  }, [t]);

  const sortedDirectories = useMemo(() => sortDirectories(directories), [directories]);
  const selectableDirectories = useMemo(
    () => sortedDirectories.filter((dir) => !isSystemDirectory(dir)),
    [sortedDirectories]
  );

  useEffect(() => {
    if (sortedDirectories.length === 0) {
      if (selectedDir) setSelectedDir('');
      return;
    }

    if (!selectedDir && sortedDirectories.length > 0) {
      setSelectedDir(sortedDirectories[0]);
      return;
    }

    if (selectedDir && !sortedDirectories.includes(selectedDir)) {
      setSelectedDir(sortedDirectories[0]);
    }
  }, [selectedDir, sortedDirectories]);

  const filteredDirectories = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return sortedDirectories;

    return sortedDirectories.filter((dir) => {
      const label = formatDirLabel(dir).toLowerCase();
      return label.includes(keyword) || dir.toLowerCase().includes(keyword);
    });
  }, [formatDirLabel, searchKeyword, sortedDirectories]);

  const treeData = useMemo(() => {
    const tree = buildDirTree(filteredDirectories);
    return toTreeData(mapDirTreeTitles(tree, formatDirLabel), sharedDirectoryPaths, t);
  }, [filteredDirectories, formatDirLabel, sharedDirectoryPaths, t]);

  const parentDirTreeData = useMemo(() => {
    const tree = buildDirTree(selectableDirectories);
    return mapDirTreeTitles(tree, (value) => value);
  }, [selectableDirectories]);

  const selectedLabel = selectedDir ? formatDirLabel(selectedDir) : t('directoryManager.selectDirectoryFirst');
  const selectedIsSystem = isSystemDirectory(selectedDir);
  const directoryPreview = useMemo(() => {
    const normalizedName = normalizeDirectoryName(newDirName);
    if (!normalizedName) return '';
    return composeDirectoryPath(newDirParent, normalizedName);
  }, [newDirName, newDirParent]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  const handleAddDirectory = async () => {
    if (!directoryPreview) {
      message.warning(t('batchActions.directoryNameRequired'));
      return;
    }
    if (containsEmoji(directoryPreview)) {
      message.warning(t('batchActions.directoryNameEmojiNotAllowed'));
      return;
    }

    setSubmitting(true);
    try {
      const success = await onAddDirectory(directoryPreview);
      if (success !== false) {
        setNewDirName('');
        setNewDirParent('');
        setSelectedDir(directoryPreview);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteDirectory = async () => {
    if (!selectedDir) {
      message.warning(t('directoryManager.selectDirectoryFirst'));
      return;
    }
    if (selectedIsSystem) {
      message.warning(t('directoryManager.protectedSystemDirectory'));
      return;
    }

    setDeleting(true);
    try {
      const success = await onDeleteDirectory(selectedDir);
      if (success !== false) {
        setSelectedDir('');
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="directory-manager">
      <div className="directory-manager__toolbar">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          value={searchKeyword}
          onChange={(event) => setSearchKeyword(event.target.value)}
          placeholder={t('directoryManager.searchPlaceholder')}
        />
        <Space wrap>
          <Tag color="blue">{t('directoryManager.directoryTreeHint', {count: sortedDirectories.length})}</Tag>
          <Button
            icon={<ReloadOutlined />}
            loading={loading || refreshing}
            onClick={handleRefresh}
          >
            {t('directoryManager.refresh')}
          </Button>
        </Space>
      </div>

      <div className="directory-manager__layout">
        <Card
          className="directory-manager__tree-card"
          title={t('directoryManager.directoryTree')}
          bodyStyle={{padding: 12}}
        >
          {treeData.length > 0 ? (
            <Tree
              key={`${searchKeyword}-${filteredDirectories.join('|')}`}
              blockNode
              defaultExpandAll
              selectedKeys={selectedDir ? [selectedDir] : []}
              treeData={treeData}
              onSelect={(keys) => setSelectedDir(String(keys[0] || ''))}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={searchKeyword ? t('directoryManager.noSearchResults') : t('directoryManager.empty')}
            />
          )}
        </Card>

        <div className="directory-manager__side">
          <Card className="directory-manager__detail-card" title={t('directoryManager.selectedDirectory')}>
            <Space direction="vertical" size={12} style={{width: '100%'}}>
              <div className="directory-manager__selected-title">
                <FolderOpenOutlined />
                <Text strong ellipsis={{tooltip: selectedLabel}}>{selectedLabel}</Text>
              </div>
              <Space wrap>
                <Tag color={selectedIsSystem ? 'gold' : 'green'}>
                  {selectedIsSystem ? t('directoryManager.systemDirectory') : t('directoryManager.userDirectory')}
                </Tag>
              </Space>
              {selectedDir && (
                <Text type="secondary" copyable={!selectedIsSystem}>
                  {t('directoryManager.selectedPath')}: {selectedDir}
                </Text>
              )}
              {selectedDir && renderSelectedActions?.(selectedDir)}
              <Popconfirm
                title={t('directoryManager.deleteConfirm', {dir: selectedLabel})}
                description={t('directoryManager.deleteHint')}
                okText={t('common.confirm')}
                cancelText={t('common.cancel')}
                disabled={!selectedDir || selectedIsSystem}
                onConfirm={handleDeleteDirectory}
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  disabled={!selectedDir || selectedIsSystem}
                  loading={deleting}
                >
                  {t('directoryManager.deleteDirectory')}
                </Button>
              </Popconfirm>
              {selectedIsSystem && (
                <Text type="secondary">{t('directoryManager.protectedSystemDirectory')}</Text>
              )}
            </Space>
          </Card>

          <Card className="directory-manager__add-card" title={t('directoryManager.addRootDirectory')}>
            <Space direction="vertical" size={12} style={{width: '100%'}}>
              <div>
                <Text type="secondary">{t('batchActions.parentDirectory')}</Text>
                <TreeSelect
                  allowClear
                  showSearch
                  treeDefaultExpandAll
                  treeNodeFilterProp="title"
                  className="directory-manager__field"
                  dropdownClassName="dir-tree-switcher-right"
                  value={newDirParent || undefined}
                  treeData={parentDirTreeData}
                  placeholder={t('batchActions.parentDirectoryPlaceholder')}
                  onChange={(value) => setNewDirParent(typeof value === 'string' ? value : '')}
                />
                <Text type="secondary">{t('directoryManager.selectParentHint')}</Text>
              </div>
              <div>
                <Text type="secondary">{t('batchActions.directoryName')}</Text>
                <Input
                  className="directory-manager__field"
                  value={newDirName}
                  onChange={(event) => setNewDirName(event.target.value)}
                  onPressEnter={handleAddDirectory}
                  placeholder={t('batchActions.directoryNamePlaceholder')}
                />
              </div>
              {directoryPreview && (
                <Text type="secondary">
                  {t('directoryManager.directoryNamePreview', {dir: directoryPreview})}
                </Text>
              )}
              <Button
                type="primary"
                icon={<FolderAddOutlined />}
                loading={submitting}
                onClick={handleAddDirectory}
              >
                {newDirParent ? t('directoryManager.addChildDirectory') : t('directoryManager.addRootDirectory')}
              </Button>
            </Space>
          </Card>
        </div>
      </div>
    </div>
  );
};

const toTreeData = (
  nodes: DirTreeNode[],
  sharedDirectoryPaths: Set<string>,
  t: (key: string) => string,
): DirectoryTreeDataNode[] => (
  nodes.map((node) => ({
    key: node.value,
    title: (
      <span className="directory-manager__tree-title">
        <span className="directory-manager__tree-label">{node.title}</span>
        {node.value !== '__all__'
          && node.value !== '全部目录'
          && sharedDirectoryPaths.has(normalizeShareDirectory(node.value)) && (
            <Tag color="success" className="directory-manager__shared-tag">
              {t('directoryShare.sharedBadge')}
            </Tag>
        )}
      </span>
    ),
    children: node.children?.length
      ? toTreeData(node.children, sharedDirectoryPaths, t)
      : undefined,
  }))
);

export default DirectoryManager;
